import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Pencil, X } from "lucide-react";

const KINDS = [
  { value: "bank", label: "Bank account" },
  { value: "upi", label: "UPI handle" },
  { value: "card", label: "Card" },
  { value: "cash", label: "Cash box" },
  { value: "other", label: "Other" },
];

const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.value, k.label]));

export default function PaymentSources() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "bank", account_identifier: "", notes: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payment-sources");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/payment-sources/${editing}`, form);
        toast.success("Payment source updated");
      } else {
        await api.post("/payment-sources", form);
        toast.success("Payment source added");
      }
      setForm({ name: "", kind: "bank", account_identifier: "", notes: "" });
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name || "",
      kind: s.kind || "bank",
      account_identifier: s.account_identifier || "",
      notes: s.notes || "",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ name: "", kind: "bank", account_identifier: "", notes: "" });
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/payment-sources/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canManage = user?.role === "master" || user?.role === "super_user";

  return (
    <div className="fade-up" data-testid="payment-sources-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
          <Wallet className="w-3 h-3" /> Treasury
        </p>
        <h1 className="font-heading text-3xl tracking-tight">Payment sources</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bank accounts, UPI handles and cards you pay suppliers from.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {canManage && (
          <Card className="p-5 md:col-span-1" data-testid="payment-source-form-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                {editing ? "Edit source" : "Add source"}
              </div>
              {editing && (
                <Button size="icon" variant="ghost" onClick={cancelEdit}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                  placeholder="e.g. HDFC current"
                  required
                  data-testid="ps-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1" data-testid="ps-kind-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KINDS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Identifier
                </Label>
                <Input
                  value={form.account_identifier}
                  onChange={(e) => setForm({ ...form, account_identifier: e.target.value })}
                  className="mt-1 font-mono-data"
                  placeholder="Last 4 digits / UPI handle"
                  data-testid="ps-ident-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1"
                  data-testid="ps-notes-input"
                />
              </div>
              <Button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#9E6D5B" }}
                className="w-full"
                data-testid="ps-save-btn"
              >
                {saving ? "Saving…" : editing ? "Save changes" : <><Plus className="w-4 h-4 mr-1" /> Add source</>}
              </Button>
            </form>
          </Card>
        )}

        <Card className={`p-0 overflow-hidden ${canManage ? "md:col-span-2" : "md:col-span-3"}`} data-testid="payment-sources-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]">
                <tr className="text-left">
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Kind</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Identifier</th>
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Notes</th>
                  {canManage && <th className="p-3 text-right" />}
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-[hsl(var(--border))]" data-testid={`ps-row-${s.id}`}>
                    <td className="p-3 font-medium">{s.name}</td>
                    <td className="p-3 text-xs">{KIND_LABEL[s.kind] || s.kind}</td>
                    <td className="p-3 text-xs font-mono-data text-muted-foreground">
                      {s.account_identifier || "—"}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground truncate max-w-[240px]" title={s.notes}>
                      {s.notes || "—"}
                    </td>
                    {canManage && (
                      <td className="p-3 text-right">
                        <Button size="icon" variant="ghost" onClick={() => startEdit(s)} data-testid={`ps-edit-${s.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`ps-delete-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {!items.length && (
                  <tr>
                    <td colSpan={canManage ? 5 : 4} className="p-10 text-center text-sm text-muted-foreground">
                      <Wallet className="w-6 h-6 mx-auto mb-2 opacity-40" />
                      {canManage
                        ? "Add your first payment source on the left."
                        : "No payment sources yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
